import * as Cesium from 'cesium'

export const DataPreparation = {
  methods: {
    processPositionAndAttitudeData() {
      // fills position and attitude data arrays
      this.data.forEach((el) => {
        this.computeSunPositionECI(el)

        this.cartesianData.push(
          el.simulationDuration,
          el.threedCartesianX,
          el.threedCartesianY,
          el.threedCartesianZ
        )

        this.sampledQuaternions.push(
          el.simulationDuration,
          el.threedAttitudeQ1,
          el.threedAttitudeQ2,
          el.threedAttitudeQ3,
          el.threedAttitudeQ0
        )
      })
    },

    computeSunPositionECI(timed_data) {
      const sampleJulianDate = Cesium.JulianDate.fromDate(
        new Date(
          this.initialDate.getTime() + timed_data.simulationDuration * 1000
        )
      )
      // Computes Sun position in ECI
      const sunPosECI =
        Cesium.Simon1994PlanetaryPositions.computeSunPositionInEarthInertialFrame(
          sampleJulianDate
        )
      this.addSunDirectionPoint(timed_data, sunPosECI)
    },

    addSunDirectionPoint(timed_data, sunPosECI) {
      const sampledSatPosCart = new Cesium.Cartesian3(
        timed_data.threedCartesianX,
        timed_data.threedCartesianY,
        timed_data.threedCartesianZ
      )
      const sunRay = new Cesium.Ray(sampledSatPosCart, sunPosECI)

      let sunDirVertex = Cesium.Ray.getPoint(sunRay, 5e3)

      this.sampledSunDir.push(
        timed_data.simulationDuration,
        sunDirVertex.x,
        sunDirVertex.y,
        sunDirVertex.z
      )
    },

    processGeneralData() {
      this.thrustDates = []
      this.eclipseDates = []
      let isBurning = false
      let isInEclipse = false
      this.generalData.forEach((el) => {
        const sampleJulianDate = Cesium.JulianDate.fromDate(
          new Date(this.initialDate.getTime() + el.simulationDuration * 1000)
        )
        if (el.remainingPropellant) {
          this.sampledRemainingPropellant.addSample(
            sampleJulianDate,
            el.remainingPropellant
          )
        }
        if (el.batteryState) {
          this.sampledBatteryState.addSample(sampleJulianDate, el.batteryState)
        }
        if (el.eclipse && !isInEclipse) {
          isInEclipse = true
          this.eclipseDates.push({
            start: Cesium.JulianDate.toIso8601(sampleJulianDate)
          })
        } else if (el.eclipse === 0 && isInEclipse) {
          isInEclipse = false
          this.eclipseDates[this.eclipseDates.length - 1].end =
            Cesium.JulianDate.toIso8601(sampleJulianDate)
        }
      })
      this.data.forEach((el) => {
        const sampleJulianDate = Cesium.JulianDate.fromDate(
          new Date(this.initialDate.getTime() + el.simulationDuration * 1000)
        )
        if (el.threedInstantConsumption > 0 && !isBurning) {
          isBurning = true
          this.thrustDates.push({
            start: Cesium.JulianDate.toIso8601(sampleJulianDate)
          })
        } else if (el.threedInstantConsumption === 0 && isBurning) {
          isBurning = false
          this.thrustDates[this.thrustDates.length - 1].end =
            Cesium.JulianDate.toIso8601(sampleJulianDate)
        }
      })
    },

    createThrustManeuvers() {
      if (this.thrustDates.length > 0) {
        if (!this.thrustDates[this.thrustDates.length - 1].end)
          this.thrustDates[this.thrustDates.length - 1].end =
            this.finalDate.toISOString()

        if (this.initialDate.toISOString() !== this.thrustDates[0].start) {
          this.thrustManeuvers.intervals.addInterval(
            Cesium.TimeInterval.fromIso8601({
              iso8601:
                this.initialDate.toISOString() +
                '/' +
                this.thrustDates[0].start,
              isStartIncluded: true,
              isStopIncluded: false,
              data: {
                burning: false,
                start: this.initialDate.toISOString(),
                end: this.thrustDates[0].start
              }
            })
          )
        }
        for (let i = 0; i < this.thrustDates.length; i++) {
          const datePair = this.thrustDates[i]
          this.thrustManeuvers.intervals.addInterval(
            Cesium.TimeInterval.fromIso8601({
              iso8601: datePair.start + '/' + datePair.end,
              isStartIncluded: true,
              isStopIncluded: false,
              data: {
                burning: true,
                start: datePair.start,
                end: datePair.end,
                id: 'burn' + '-' + i
              }
            })
          )
          if (i < this.thrustDates.length - 1) {
            this.thrustManeuvers.intervals.addInterval(
              Cesium.TimeInterval.fromIso8601({
                iso8601: datePair.end + '/' + this.thrustDates[i + 1].start,
                isStartIncluded: true,
                isStopIncluded: false,
                data: {
                  burning: false,
                  start: this.thrustDates[i + 1].start,
                  end: datePair.end,
                  id: 'burn' + '-' + i
                }
              })
            )
          }
        }

        this.thrustDates.forEach((datePair, index) => {
          this.satellitesEvents[0].burns.push({
            id: 'burn' + '-' + index,
            start: datePair.start,
            end: datePair.end,
            progress: 0
          })
        })
      }
    },

    createEclipseIntervals() {
      if (this.eclipseDates.length > 0) {
        if (!this.eclipseDates[this.eclipseDates.length - 1].end)
          this.eclipseDates[this.eclipseDates.length - 1].end =
            this.finalDate.toISOString()

        if (this.initialDate.toISOString() !== this.eclipseDates[0].start) {
          this.eclipseIntervals.intervals.addInterval(
            Cesium.TimeInterval.fromIso8601({
              iso8601:
                this.initialDate.toISOString() +
                '/' +
                this.eclipseDates[0].start,
              isStartIncluded: true,
              isStopIncluded: false,
              data: {
                inEclipse: false,
                start: this.initialDate.toISOString(),
                end: this.eclipseDates[0].start
              }
            })
          )
        }
        for (let i = 0; i < this.eclipseDates.length; i++) {
          const datePair = this.eclipseDates[i]
          this.eclipseIntervals.intervals.addInterval(
            Cesium.TimeInterval.fromIso8601({
              iso8601: datePair.start + '/' + datePair.end,
              isStartIncluded: true,
              isStopIncluded: false,
              data: {
                inEclipse: true,
                start: datePair.start,
                end: datePair.end,
                id: 'eclipse' + '-' + i
              }
            })
          )
          if (i < this.eclipseDates.length - 1) {
            this.eclipseIntervals.intervals.addInterval(
              Cesium.TimeInterval.fromIso8601({
                iso8601: datePair.end + '/' + this.eclipseDates[i + 1].start,
                isStartIncluded: true,
                isStopIncluded: false,
                data: {
                  inEclipse: false,
                  start: this.eclipseDates[i + 1].start,
                  end: datePair.end,
                  id: 'eclipse' + '-' + i
                }
              })
            )
          }
        }
        this.eclipseDates.forEach((datePair, index) => {
          this.satellitesEvents[0].eclipses.push({
            id: 'eclipse' + '-' + index,
            start: datePair.start,
            end: datePair.end
          })
        })
      }
    }
  }
}
